import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ListAltIcon from '@material-ui/icons/ListAlt';
import DescriptionIcon from '@material-ui/icons/Description';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { Pagination } from '@material-ui/lab';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import cuid from 'cuid';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '@super-template/lab/src/ErrorFallback';
import { useKeycloak } from '@react-keycloak/web';

import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

import messages from 'messages.js';
import {
  fetchApplicants,
  deleteApplicant,
  fetchManagers,
  fetchSection,
  getSectionForManager,
} from 'api';
import { EnsembleListContext } from 'contexts/EnsembleListContext';
import DeleteButton from '../DeleteButton';
import OrgAdminActionDialog from 'components/OrgAdminActionDialog/OrgAdminActionDialog';

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    height: '50px',
    background: theme.palette.common.greyLight,
    color: theme.palette.common.black,
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  icon: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontSize: '1.5rem',
    lineHeight: '1',
    verticalAlign: 'middle',
  },
  tabletitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1.5rem',
    lineHeight: '1',
    verticalAlign: 'middle',
  },
  sectiontable: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  tableRow: {
    border: '1px solid rgba(0,0,0,0.2)',
  },
  tableCell: {
    padding: '10px 10px',
  },
  tableButton: {
    color: theme.palette.hallinto.blueDark,
  },
  actionFooter: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const ApplicantHeader = ({ setSearchTerm }) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <div className={classes.tableHeader}>
      <Typography variant="h2" color="primary" className={classes.tabletitle}>
        <ListAltIcon className={classes.icon} />
        {intl.formatMessage(messages.applicant_title)}
      </Typography>
      <TextField
        label="Haku"
        variant="outlined"
        onChange={(e) => setSearchTerm(e.target.value)}
        margin="dense"
        size="small"
        style={{ marginLeft: 'auto' }}
      />
    </div>
  );
};

export const ApplicantList = () => {
  const { keycloak } = useKeycloak();
  const hasOrganizationAdminRole =
    keycloak.hasResourceRole('organizationAdmin');
  const classes = useStyles();
  const intl = useIntl();
  const location = useLocation();
  const { sectionId } = useParams();
  const [applicantList, setApplicantList] = useState([]);
  const [filteredApplicantList, setFilteredApplicantList] = useState([]);
  const [searchedApplicantList, setSearchedApplicantList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [dialogType, setDialogType] = useState('');
  const [section, setSection] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const PER_PAGE = 10;
  const [managerList, setManagerList] = useState([]);
  const { titleOptions } = useContext(EnsembleListContext);
  const IS_STUDENT_SECTION = !!(
    section &&
    section[0] &&
    section[0].externalType.includes('5')
  );

  useEffect(() => {
    getApplicantList();
    getManagerList();
    // eslint-disable-next-line
  }, [location]);

  const resolveApplicantTitleLabel = useCallback(
    (titleCode) => {
      if (!titleCode) {
        return '';
      }

      const matchingOption = titleOptions.find(
        (option) => option.code === titleCode
      );
      return matchingOption ? matchingOption.label : titleCode;
    },
    [titleOptions]
  );

  const getApplicantList = () => {
    fetchApplicants(sectionId).then((response) => {
      setApplicantList(response.data);
      setFilteredApplicantList(response.data);
    });
  };
  const getManagerList = () => {
    fetchManagers(sectionId).then((response) => {
      setManagerList(response.data);
    });
  };

  const handleDelete = (id) => {
    const indexInApplicantList = applicantList.findIndex(
      (item) => item.id === id
    );

    if (indexInApplicantList > -1) {
      deleteApplicant(applicantList[indexInApplicantList].id, sectionId)
        .then(() => {
          const newApplicantList = [...applicantList];
          newApplicantList.splice(indexInApplicantList, 1);
          setApplicantList(newApplicantList);

          const indexInFilteredApplicantList = filteredApplicantList.findIndex(
            (item) => item.id === id
          );
          if (indexInFilteredApplicantList > -1) {
            const newFilteredApplicantList = [...filteredApplicantList];
            newFilteredApplicantList.splice(indexInFilteredApplicantList, 1);
            setFilteredApplicantList(newFilteredApplicantList);
          }
        })
        .catch(() => {});
    }
    setOpenDropdownId(null);
  };

  const handleEditClick = (applicant) => {
    setSelectedApplicant(applicant);
    setDialogType('applicant');
    setDialogOpen(true);
    setOpenDropdownId(null);
  };

  const handleSendApplicationClick = (applicant) => {
    setSelectedApplicant(applicant);
    setDialogType('application');
    setDialogOpen(true);
    setOpenDropdownId(null);
  };

  const handleCreateClick = () => {
    setSelectedApplicant(null);
    setDialogType('applicant');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const getSection = useCallback(() => {
    const fetchSectionMethod = hasOrganizationAdminRole
      ? fetchSection
      : getSectionForManager;

    fetchSectionMethod(sectionId).then((response) => {
      setSection([response.data]);
    });
  }, [sectionId, hasOrganizationAdminRole]);

  useEffect(() => {
    if (section === null) {
      getSection();
    }
    // eslint-disable-next-line
  }, [getSection]);

  useEffect(() => {
    if (searchTerm.length < 3) {
      setSearchedApplicantList(filteredApplicantList);
      return;
    }
    const searchList = filteredApplicantList.filter(
      (applicant) =>
        (applicant.firstName != null &&
          applicant.firstName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (applicant.surName != null &&
          applicant.surName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (applicant.givenName != null &&
          applicant.givenName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        resolveApplicantTitleLabel(applicant.title)
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
    setSearchedApplicantList(searchList);
    setPage(1);
  }, [filteredApplicantList, searchTerm, resolveApplicantTitleLabel]);

  // Action Menus
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = React.useRef({}); // store refs to each button

  // Weird implementation, but Menu/MenuList did not work with an async list map
  const handleToggle = (id) => {
    const button = buttonRefs.current[id];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className={classes.sectiontable}>
        <ApplicantHeader setSearchTerm={setSearchTerm} />
        <TableContainer>
          <Table className={classes.sectiontable}>
            <TableHead>
              <TableRow>
                <TableCell align="left">Henkilötunnus</TableCell>
                <TableCell align="left">Nimi</TableCell>
                <TableCell align="left">Nimike</TableCell>
                <TableCell align="left">Sähköposti</TableCell>
                <TableCell align="left">Hakuoikeuden eräpäivä</TableCell>
                <TableCell align="left">Oikeuden eräpäivä</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchedApplicantList
                .slice((page - 1) * PER_PAGE, page * PER_PAGE)
                .map((applicant) => (
                  <TableRow className={classes.tableRow} key={cuid()}>
                    <TableCell className={classes.tableCell}>
                      {applicant.socialSecurityNumber}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {(applicant.firstName
                        ? applicant.firstName
                        : applicant.givenName) || ''}{' '}
                      {applicant.surName || ''}
                    </TableCell>

                    <TableCell className={classes.tableCell}>
                      {resolveApplicantTitleLabel(applicant.title)}
                    </TableCell>

                    <TableCell className={classes.tableCell}>
                      {applicant.email}
                    </TableCell>

                    <TableCell className={classes.tableCell}>
                      {applicant.applicantsLicenseExpiryDate}
                    </TableCell>

                    <TableCell
                      className={classes.tableCell}
                      style={{ width: '150px' }}
                    >
                      {applicant.validityPeriodExpiryDate}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        ref={(el) => (buttonRefs.current[applicant.id] = el)}
                        onClick={() => handleToggle(applicant.id)}
                      >
                        {intl.formatMessage(messages.general_actions)}
                        {openDropdownId === applicant.id ? (
                          <KeyboardArrowUpIcon
                            style={{
                              fontSize: 30,
                              position: 'relative',
                              top: '0px',
                            }}
                          />
                        ) : (
                          <KeyboardArrowDownIcon
                            style={{
                              fontSize: 30,
                              position: 'relative',
                              top: '0px',
                            }}
                          />
                        )}
                      </Button>
                      {openDropdownId === applicant.id && (
                        <ClickAwayListener
                          onClickAway={() => setOpenDropdownId(null)}
                        >
                          <Paper
                            style={{
                              position: 'absolute',
                              top: dropdownPosition.top + 2,
                              left: dropdownPosition.left,
                              zIndex: 10,
                              borderRadius: 6,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => {
                                handleEditClick(applicant);
                              }}
                              style={{
                                padding: '8px 16px',
                                marginBottom: '2px',
                                minWidth: '185px',
                                textAlign: 'center',
                              }}
                            >
                              {intl.formatMessage(messages.general_edit)}
                            </Button>
                            {IS_STUDENT_SECTION && (
                              <>
                                <br />
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    handleSendApplicationClick(applicant)
                                  }
                                  style={{
                                    padding: '8px 16px',
                                    minWidth: '185px',
                                    textAlign: 'center',
                                    marginBottom: '2px',
                                  }}
                                >
                                  {intl.formatMessage(
                                    messages.send_application
                                  )}
                                </Button>
                              </>
                            )}
                            <br />
                            <DeleteButton
                              id={applicant.id}
                              handleDeleteCallback={handleDelete}
                              className={classes.button}
                              style={{
                                padding: '8px 16px',
                                minWidth: '185px',
                                textAlign: 'center',
                                marginBottom: '2px',
                              }}
                            >
                              {intl.formatMessage(messages.general_delete)}
                            </DeleteButton>
                          </Paper>
                        </ClickAwayListener>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {applicant.fileName && (
                        <Tooltip
                          title={
                            <span style={{ fontSize: 20 }}>
                              {applicant.fileName}
                              <br />
                              <br />
                              {applicant.massUploadErrorMessage}
                            </span>
                          }
                        >
                          <DescriptionIcon
                            style={{
                              fontSize: 30,
                              position: 'relative',
                              top: '8px',
                            }}
                            color={
                              applicant.massUploadErrorMessage
                                ? 'secondary'
                                : ''
                            }
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className={classes.actionFooter}>
          <Pagination
            count={Math.ceil(searchedApplicantList.length / PER_PAGE)}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
          <ButtonGroup>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                setDialogType('csv');
                setDialogOpen(true);
              }}
            >
              {intl.formatMessage(messages.export_external_csv)}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleCreateClick}
            >
              {intl.formatMessage(messages.add)}
            </Button>
          </ButtonGroup>
        </div>
        <OrgAdminActionDialog
          dialogType={dialogType}
          open={dialogOpen}
          uploadType={'applicant'}
          handleClose={handleCloseDialog}
          applicant={selectedApplicant}
          sectionId={sectionId}
          isEditing={selectedApplicant !== null}
          updateListCallback={() => getApplicantList()}
          sectionManagerlist={managerList}
          section={section}
          titleOptions={titleOptions}
          applicantTitle={resolveApplicantTitleLabel(selectedApplicant?.title)}
        />
      </div>
    </ErrorBoundary>
  );
};
